import { gql } from "@apollo/client";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import Button from "components/common/button/Button";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import If from "components/common/If";
import Translate from "components/common/Translate";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteSaleTemplate } from "types/agriculture/sale-types";
import { NotNull } from "types/common-types";
import { DeleteSaleTemplateMutation, DeleteSaleTemplateMutationVariables, SaleTemplateDatalistQuery, SaleTemplateDatalistQueryVariables, SortEnumType } from 'types/graphql';
import { Roles } from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";

const DELETE_URL_HASH = "delete-sale-template-item";

const saleTemplateDatalistQuery = gql`
query SaleTemplateDatalist($where: SaleTemplateFilterInput, $order: [SaleTemplateSortInput!], $skip: Int, $take: Int) {
    saleTemplates {
      items(where:$where, order: $order, skip:$skip, take:$take) {
        id,
        name
      }
    }
  }
`

type SaleTemplateDatalistProps = {
    newUrl?: string | AppRoutes
    onClickUrl?: (item: NotNull<NotNull<SaleTemplateDatalistQuery['saleTemplates']>['items']>[0]) => string
}


const SaleTemplateDatalist = (props: SaleTemplateDatalistProps) => {
    const [t] = useTranslation();
    const [state, setState] = useState<ComponentStateTypes>('loading');
    const [data, setData] = useState<NotNull<SaleTemplateDatalistQuery['saleTemplates']>['items']>();
    const context = useAppContext();

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setState('loading');

        try {
            const response = await graph<SaleTemplateDatalistQuery, SaleTemplateDatalistQueryVariables>({
                query: saleTemplateDatalistQuery,
                variables: {
                    order: [
                        {
                            name: SortEnumType.Asc
                        }
                    ]
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            if (!response.data) setState('nodata');

            setState('');
            setData(response.data!.saleTemplates!.items);
        } catch (error) {
            setState('error');
            context.notify('error', 'error.loading-data')
            throw error;
        }
    }
    
    const onDelete = async () => {
        const response = await graph<DeleteSaleTemplateMutation, DeleteSaleTemplateMutationVariables>({
            query: deleteSaleTemplate,
            variables: {
                id: Number(context.modal.getData(DELETE_URL_HASH))
            }
        });

        if(response.errors?.length || !response.data?.saleTemplateDelete) {
            context.notify('error', 'request.delete.error');
        } else {
            context.notify('success', 'request.delete.success');
        }

        await loadData();
    }

    return (
        <>
            <ComponentState state={state}>
                <ul className=" px-2 md:px-0">
                    <If condition={context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                        <li className="border-b theme-border pb-1 py-4 md:py-2">
                            <AppLink to={props.newUrl || AppRoutes.TEMPLATE_SALE_FORM}>
                                <Translate i18nKey="button.add-new" />
                            </AppLink>
                        </li>
                    </If>
                    {data?.map(i => {
                        return (
                            <li key={i!.id} className="border-b border-transparent hover:border-primary">
                                <If condition={!props.onClickUrl}>
                                    <div className="flex">
                                        <div className="w-full my-auto">
                                            {i?.name}
                                        </div>
                                        <If condition={context.userHasRole(Roles.AGRICULTURE_SALES_WRITE)}>
                                            <div>
                                                <div className="grid-cols-2 gap-4 grid text text-center">
                                                    <div className="p-2 my-auto text-center">
                                                        <AppLink className="link" to={AppRoutes.TEMPLATE_SALE_EDIT} params={{ id: i?.id }}>
                                                            <FontAwesomeIcon icon={faPen} />
                                                        </AppLink>
                                                    </div>
                                                    <div className="p-2 my-auto text-center">
                                                        <Button 
                                                            className="link text-danger" 
                                                            title="button.delete"
                                                            onClick={() => context.modal.open(DELETE_URL_HASH, i?.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </If>
                                    </div>
                                </If>
                                <If condition={props.onClickUrl}>
                                    <div className="flex">
                                        <div className="w-full my-auto py-4 md:py-2">
                                            <AppLink to={(props.onClickUrl && props.onClickUrl(i)) || ''}>
                                                <>{i?.name}</>
                                            </AppLink>
                                        </div>
                                    </div>
                                </If>
                            </li>
                        )
                    })}
                </ul>
            </ComponentState>
            <ConfirmationModal text={t('request.delete.confirmation')} name={DELETE_URL_HASH} onReject onConfirm={onDelete} />
        </>
    );
};

export default SaleTemplateDatalist;
